<template>
  <div
    v-if="isRecentSearchesVisible"
    ref="recentSearchesContainerRef"
    class="recent-searches-container"
    role="listbox"
    tabindex="0"
    aria-labelledby="recent-searches-title"
    :aria-activedescendant="activeDescendantId"
    @keydown="handleKeydownOnListbox($event)"
    @focus="handleFocusOnListbox()"
  >
    <h6
      id="recent-searches-title"
      class="recent-searches-title"
    >
      {{ $t('search.recentSearches.title') }}
    </h6>
    <ul
      class="recent-searches-list"
      role="presentation"
    >
      <li
        v-for="(search, index) in recentSearches"
        :id="`recent-search-${index}`"
        :key="search"
        class="recent-searches-list-item"
        :class="{ 'is-focused': focusedIndex === index }"
        role="option"
        :aria-selected="focusedIndex === index"
        @mouseover="handleMouseOverOnListItem(index)"
        @click="handleRecentSearchClick(search, index)"
      >
        <span>{{ search }}</span>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { watch } from 'vue';
import { onClickOutside } from '@vueuse/core';

import { useRecentSearches } from '~/composables/useRecentSearches';
import { useAccessibleDropdown } from '~/composables/useAccessibleDropdown';

const props = defineProps({
  isSearchInputFocused: {
    type: Boolean,
    default: false,
  },
  searchInputRef: {
    type: [Object, null],
    required: true,
  },
});

const emit = defineEmits(['selected-recent-search']);

const { $t } = useNuxtApp();
const { isRecentSearchesVisible, recentSearches, closeRecentSearchesDropdown } = useRecentSearches(
  toRef(props, 'isSearchInputFocused'),
);

const recentSearchesContainerRef = useTemplateRef('recentSearchesContainerRef');

const { focusedIndex, activeDescendantId, handleListboxKeydown, handleListboxFocus, clearFocusedIndex } =
  useAccessibleDropdown();

// Reset focus when component becomes visible
watch(isRecentSearchesVisible, (isVisible) => {
  if (isVisible) {
    clearFocusedIndex();
  }
});

onClickOutside(recentSearchesContainerRef, closeRecentSearchesDropdown, {
  ignore: [toRef(props, 'searchInputRef')],
});

function handleRecentSearchClick(search) {
  emit('selected-recent-search', search);
  closeRecentSearchesDropdown();
}

function handleKeydownOnListbox(event) {
  handleListboxKeydown(event, recentSearches.value, 'recent-search', {
    onSelect: (index) => {
      const selectedSearch = recentSearches.value[index];
      handleRecentSearchClick(selectedSearch);
    },
    optionNameGetter: (option) => option,
    allowMultiSelect: false, // Single-select behavior for navigation
    returnFocusSelector: '.search-input',
  });
}

function handleFocusOnListbox() {
  handleListboxFocus(recentSearches.value, 'recent-search', () => false);
}

function handleMouseOverOnListItem(index) {
  focusedIndex.value = index;
}
</script>

<style lang="scss" scoped>
.recent-searches-container {
  --color-grey-500: #949494;
  --z-index-recent-searches: 2;

  position: absolute;
  left: 0;
  right: 0;
  top: 80px;
  background: var(--primary-white);
  border-radius: 8px;
  border: 1px solid var(--color-grey-10);
  box-shadow: 0px -6px 20px 0px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
  padding: 12px 0px;
  margin: -10px 16px 0 16px;
  z-index: var(--z-index-recent-searches);

  .recent-searches-title {
    color: var(--color-grey-500);
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 8px 0 0 16px;
    line-height: 16px;
  }

  .recent-searches-list {
    list-style: none;
    padding: 0;
    margin: 0;

    &-item {
      padding: 12px 16px;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: 0.6px;
      cursor: pointer;

      &:hover,
      &.is-focused {
        background: color-mix(in srgb, var(--primary-button-background) 10%, #fff);
      }

      span {
        padding-left: 24px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        line-clamp: 2;
        overflow: hidden;
      }
    }
  }
}
</style>
